/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { CodeSnippet, FormLabel, TextInput } from 'carbon-components-react';
import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { selectIsNewIa } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { copyContentToClipboard } from '@ibm-watson/shared/modules/ia/util/clipboard/clipboard';
import { useAppSelector } from '@ibm-watson/shared/store';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  GENESYS_BYOSD_TYPE,
  INTELEPEER_BYOSD_TYPE,
  TWILIO_BYOSD_TYPE
} from '../../../util/channelTypes';
import { ControlledCheckbox } from '../../common/ControlledCheckbox';
import { sharedMessages } from '../../messages';
import { links } from '../links';
import { FIELDS } from '../schema';
import { SipTrunk as messages } from './messages';

interface SipTrunkProps {
  sipURI: string;
  selectedServiceDesk: string;
}
export const SipTrunk = ({ sipURI, selectedServiceDesk }: SipTrunkProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue, updateFieldValue } = useFormHelpers(formMethods);

  const isNewIA = useAppSelector(selectIsNewIa);

  const sipCustomHeaders = getFieldValue(FIELDS.SIP.CUSTOM_INVITE_HEADERS);
  const [customInviteHeaders, setCustomInviteHeaders] = useState<string>(
    sipCustomHeaders?.map(item => item.name).join(', ') || ''
  );

  const [editingCustomInviteHeader, setEditingCustomInviteHeader] =
    useState(false);

  useEffect(() => {
    if (editingCustomInviteHeader) {
      const headers = customInviteHeaders
        .split(',')
        .filter(item => item.trim() !== '')
        .map(item => ({ name: item.trim() }));

      updateFieldValue(FIELDS.SIP.CUSTOM_INVITE_HEADERS, headers);
    }
  }, [customInviteHeaders, editingCustomInviteHeader, updateFieldValue]);

  const getLearnMoreLink = () => {
    switch (selectedServiceDesk) {
      case GENESYS_BYOSD_TYPE:
        return isNewIA
          ? links.genesysServiceDeskNewIA
          : links.genesysServiceDesk;
      case INTELEPEER_BYOSD_TYPE:
        return links.intelepeerServiceDesk;
      case TWILIO_BYOSD_TYPE:
        return isNewIA
          ? links.sipTrunkProvidersNewIA
          : links.learnMoreOtherSipLink;
      default:
        return isNewIA
          ? links.sipTrunkProvidersNewIA
          : links.learnMoreOtherSipLink;
    }
  };

  const getDescription = () => {
    switch (selectedServiceDesk) {
      case GENESYS_BYOSD_TYPE:
        return messages.descriptionGenesys;
      case INTELEPEER_BYOSD_TYPE:
        return messages.descriptionIntelepeer;
      case TWILIO_BYOSD_TYPE:
        return messages.descriptionTwilio;
      default:
        return messages.descriptionBYO;
    }
  };

  return (
    <form>
      <div className="TabContent__field-container">
        <div className="TabContent__subtitle TabContent__link-larger-text">
          <FormattedMessageWithLink
            id="sip-learn-more"
            url={getLearnMoreLink()}
            {...getDescription()}
          />
        </div>
        <div className="TabContent__spacer8" />
        <FormLabel>
          <FormattedMessage id="sip-label" {...messages.sipLabel} />
        </FormLabel>
        <CodeSnippet
          id="sip-trunking-uri"
          role="figure"
          aria-label={t(messages.sipLabel)}
          feedback={t(sharedMessages.copied)}
          copyButtonDescription={t(sharedMessages.copyToClipboard)}
          copyLabel={t(sharedMessages.copyToClipboard)}
          onClick={() => copyContentToClipboard(sipURI)}
          type="single">
          {sipURI}
        </CodeSnippet>
        <div className="TabContent__instructions-no-spacer">
          <div className="TabContent__description" />
          <h4>
            <FormattedMessage id="sip-subtitle" {...messages.subtitle} />
          </h4>
          <div className="TabContent__description" />
          <div className="TabContent__field-container">
            <TextInput
              id="custom-invite-headers"
              autoComplete="off"
              placeholder={t(messages.sipTrunkingPlaceholder)}
              labelText={t(messages.sipTrunkingLabel)}
              helperText={t(messages.sipTrunkingHelperText)}
              value={customInviteHeaders}
              onChange={e => {
                setCustomInviteHeaders(e.target.value);
                setEditingCustomInviteHeader(true);
              }}
              onBlur={() => {
                setEditingCustomInviteHeader(false);
              }}
            />
          </div>
          <div className="TabContent__field-container">
            <ControlledCheckbox
              field={FIELDS.SIP.SEND_PROVISIONAL_RESPONSE}
              reversePolarity
              labelText={t(messages.disableRing)}
            />
            <ControlledCheckbox
              field={FIELDS.SIP.PUT_CALLER_ON_HOLD_ON_TRANSFER}
              reversePolarity
              labelText={t(messages.doNotPlaceOnHold)}
            />
          </div>
        </div>
      </div>
    </form>
  );
};
